import React, { useState, useMemo } from 'react';
import { Layout } from '../components/layout/Layout';
import {
  RFQState,
  initialRFQState,
  SERVICE_TYPES,
  SAND_PRODUCTION_OPTIONS,
  H2S_OPTIONS,
  ENVIRONMENT_OPTIONS,
  DURATION_OPTIONS,
  SERVICE_MODEL_OPTIONS,
  COUNTRY_OPTIONS,
  LOCATION_TYPE_OPTIONS,
  TIMELINE_OPTIONS,
  STEP_LABELS,
  TOTAL_STEPS,
} from '../components/rfq/rfqConfig';
import { runRuleEngine, RuleEngineOutput, formatRFQData, formatRuleEngineOutput } from '../components/rfq/ruleEngine';
import { generateAIProposal, GeneratedOutput } from '../components/rfq/proposalGenerator';
import { DocumentRenderer } from '../components/rfq/DocumentRenderer';
import { ReviewAdjustStep, ClientAdjustments, EquipmentAdjustment, getDefaultAssumptions, isEquipmentLocked, buildAdjustmentSummaryText } from '../components/rfq/ReviewAdjustStep';
import { runEngineeringValidation, ValidationReport } from '../components/rfq/engineeringValidator';
import { ValidationPanel } from '../components/rfq/ValidationPanel';
import { generateProposalDocument, generateProposalPDF, ProposalDocument as ProposalDocType } from '../components/rfq/proposalDocumentGenerator';
import { ProposalDocumentViewer } from '../components/rfq/ProposalDocumentViewer';
import { isDemoMode, getDefaultDemo, DEMO_SCENARIOS, DemoScenario } from '../components/rfq/demoMode';
import { EmailPreview } from '../components/rfq/EmailPreview';
import { ArrowLeft, ArrowRight, Send, Shield, Lock, CheckCircle, AlertTriangle, Cpu, FileText, Download, ClipboardList, BookOpen, Play, Mail } from 'lucide-react';

import { BACKEND_URL } from '../config/api';

export const Contact = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [rfqState, setRfqState] = useState<RFQState>(initialRFQState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [output, setOutput] = useState<GeneratedOutput | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState('');
  const [rfqId, setRfqId] = useState('');
  const [clientAdjustments, setClientAdjustments] = useState<ClientAdjustments>({
    equipment: [],
    assumptions: [],
    additions: [],
  });
  const [validationReport, setValidationReport] = useState<ValidationReport | null>(null);
  const [proposalDoc, setProposalDoc] = useState<ProposalDocType | null>(null);
  const [showProposal, setShowProposal] = useState(false);
  const [demoActive, setDemoActive] = useState(false);
  const [selectedDemo, setSelectedDemo] = useState<DemoScenario | null>(null);

  // Check for demo mode on mount
  React.useEffect(() => {
    if (isDemoMode()) {
      const demo = getDefaultDemo();
      setSelectedDemo(demo);
      setDemoActive(true);
      setRfqState(demo.state);
      // Skip to review step to show full workflow instantly
      setCurrentStep(6);
    }
  }, []);

  const updateField = (field: keyof RFQState, value: string) => {
    setRfqState(prev => ({ ...prev, [field]: value }));
  };

  // Run rule engine reactively
  const ruleEngineOutput: RuleEngineOutput = useMemo(() => {
    return runRuleEngine(rfqState);
  }, [rfqState]);

  // Build live RFQ summary
  const liveRFQSummary = useMemo(() => {
    const lines: string[] = [];
    const serviceLabel = SERVICE_TYPES.find(s => s.id === rfqState.service)?.label;
    if (serviceLabel) lines.push(`Service: ${serviceLabel}`);
    if (rfqState.wellheadPressure) lines.push(`Pressure: ${rfqState.wellheadPressure} PSI`);
    if (rfqState.oilRate) lines.push(`Oil Rate: ${rfqState.oilRate} BOPD`);
    if (rfqState.gasRate) lines.push(`Gas Rate: ${rfqState.gasRate} MMSCFD`);
    if (rfqState.temperature) lines.push(`Temperature: ${rfqState.temperature}°C`);
    if (rfqState.sandProduction) lines.push(`Sand: ${rfqState.sandProduction}`);
    if (rfqState.h2sPresence) lines.push(`H₂S: ${rfqState.h2sPresence === 'yes' ? 'Yes' : 'No'}`);
    if (rfqState.environment) lines.push(`Environment: ${rfqState.environment === 'onshore' ? 'Onshore' : 'Offshore'}`);
    if (rfqState.serviceModel) lines.push(`Model: ${rfqState.serviceModel === 'rental' ? 'Rental' : 'Full Service'}`);
    if (rfqState.duration) lines.push(`Duration: ${rfqState.duration}`);
    if (rfqState.country) {
      const countryLabel = COUNTRY_OPTIONS.find(c => c.id === rfqState.country)?.label;
      if (countryLabel) lines.push(`Country: ${countryLabel}`);
    }
    if (rfqState.locationType) lines.push(`Location: ${rfqState.locationType}`);
    if (rfqState.timeline) lines.push(`Timeline: ${rfqState.timeline}`);
    return lines;
  }, [rfqState]);

  const canProceed = (): boolean => {
    switch (currentStep) {
      case 0: return rfqState.service !== '';
      case 1: return true;
      case 2: return rfqState.environment !== '' && rfqState.serviceModel !== '';
      case 3: return rfqState.country !== '';
      case 4: return rfqState.timeline !== '';
      case 5: return true;
      case 6: {
        // Ensure all removal requests have reasons
        const removals = clientAdjustments.equipment.filter(e => e.status === 'request-removal');
        return removals.every(r => r.removalReason.trim() !== '');
      }
      case 7: {
        // Can only proceed (submit) if validation allows it
        return validationReport?.canSubmit ?? false;
      }
      default: return false;
    }
  };

  const handleNext = () => {
    if (currentStep < TOTAL_STEPS && canProceed()) {
      const nextStep = currentStep + 1;
      // Run engineering validation when entering step 7
      if (nextStep === 7) {
        const report = runEngineeringValidation(rfqState, ruleEngineOutput, clientAdjustments);
        setValidationReport(report);
      }
      // Initialize adjustments when entering step 6 (Review & Adjust)
      if (nextStep === 6 && clientAdjustments.equipment.length === 0) {
        const pressure = parseFloat(rfqState.wellheadPressure) || 0;
        const equipAdj: EquipmentAdjustment[] = ruleEngineOutput.equipmentList.map(item => ({
          name: item.name,
          specification: item.specification,
          category: item.category,
          status: 'included' as const,
          removalReason: '',
          isLocked: isEquipmentLocked(item, rfqState.h2sPresence, pressure),
        }));
        const assumptions = getDefaultAssumptions(ruleEngineOutput, rfqState.h2sPresence, rfqState.environment);
        setClientAdjustments({ equipment: equipAdj, assumptions, additions: [] });
      }
      setCurrentStep(nextStep);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  // Generate fallback output — Professional Engineering Document Format
  const generateFallbackOutput = (): GeneratedOutput => {
    const ruleOutput = runRuleEngine(rfqState);
    const serviceLabel = SERVICE_TYPES.find(s => s.id === rfqState.service)?.label || rfqState.service;
    const countryLabel = COUNTRY_OPTIONS.find(c => c.id === rfqState.country)?.label || rfqState.country;
    const envLabel = rfqState.environment === 'offshore' ? 'Offshore' : 'Onshore';
    const modelLabel = rfqState.serviceModel === 'full-service' ? 'Full Service (Equipment + Crew + Supervision)' : 'Rental (Equipment Only)';
    const durationLabel = rfqState.duration === 'short-term' ? '14 days (Short-term)' : '60 days (Long-term)';
    const timelineLabel = rfqState.timeline === 'immediate' ? 'Immediate mobilization required' : rfqState.timeline === '1-2-weeks' ? '1–2 weeks lead time' : 'Planned deployment';
    const pressure = parseFloat(rfqState.wellheadPressure) || 0;
    const gasRate = parseFloat(rfqState.gasRate) || 0;

    // Build conditions table for RFQ
    const condLines: string[] = [];
    condLines.push('Parameter | Value | Unit');
    condLines.push('--- | --- | ---');
    if (rfqState.wellheadPressure) condLines.push(`Wellhead Pressure | ${rfqState.wellheadPressure} | PSI`);
    if (rfqState.oilRate) condLines.push(`Oil Production Rate | ${rfqState.oilRate} | BOPD`);
    if (rfqState.gasRate) condLines.push(`Gas Production Rate | ${rfqState.gasRate} | MMSCFD`);
    if (rfqState.temperature) condLines.push(`Flowing Temperature | ${rfqState.temperature} | °C`);
    if (rfqState.sandProduction) condLines.push(`Sand Production | ${rfqState.sandProduction} | Level`);
    condLines.push(`H₂S Presence | ${rfqState.h2sPresence === 'yes' ? 'Confirmed' : 'Not indicated'} | —`);
    condLines.push(`Environment | ${envLabel} | —`);

    const rfqDraft = [
      '1. SERVICE REQUESTED',
      '',
      `${serviceLabel} — ${envLabel} operations, ${countryLabel}.`,
      '',
      '2. OPERATIONAL CONDITIONS',
      '',
      condLines.join('\n'),
      '',
      '3. SCOPE DEFINITION',
      '',
      `Service model: ${modelLabel}. Operational duration: ${durationLabel}. Environment: ${envLabel} facility in ${countryLabel}.`,
      '',
      '4. LOCATION & LOGISTICS',
      '',
      `Country: ${countryLabel}`,
      `Location Type: ${rfqState.locationType || 'Not specified'}`,
      rfqState.locationType === 'remote' || rfqState.locationType === 'desert'
        ? 'Self-contained logistics and power generation required for remote access.'
        : 'Standard logistics coordination assumed.',
      '',
      '5. REQUIRED TIMELINE',
      '',
      `${timelineLabel}. Mobilization window subject to equipment availability confirmation.`,
      '',
      '6. SPECIAL REQUIREMENTS',
      '',
      ruleOutput.flags.length > 0
        ? ruleOutput.flags.map(f => f.replace(/⚠️\s*/g, '')).join('\n')
        : 'No special flags identified based on stated conditions.',
      rfqState.additionalNotes ? `\nClient Notes: ${rfqState.additionalNotes}` : '',
    ].join('\n');

    // Group equipment by category
    const primaryEquip = ruleOutput.equipmentList.filter(e => e.category === 'primary');
    const safetyEquip = ruleOutput.equipmentList.filter(e => e.category === 'safety');
    const supportEquip = ruleOutput.equipmentList.filter(e => e.category === 'support');
    const logisticsEquip = ruleOutput.equipmentList.filter(e => e.category === 'logistics');

    let equipSection = '';
    if (primaryEquip.length > 0) {
      equipSection += `[PRIMARY SYSTEMS]\n${primaryEquip.map(e => `${e.name} — ${e.specification}`).join('\n')}\n\n`;
    }
    if (safetyEquip.length > 0) {
      equipSection += `[SAFETY SYSTEMS]\n${safetyEquip.map(e => `${e.name} — ${e.specification}`).join('\n')}\n\n`;
    }
    if (supportEquip.length > 0) {
      equipSection += `[SUPPORT & CREW]\n${supportEquip.map(e => `${e.name} — ${e.specification}`).join('\n')}\n\n`;
    }
    if (logisticsEquip.length > 0) {
      equipSection += `[LOGISTICS & SITE SUPPORT]\n${logisticsEquip.map(e => `${e.name} — ${e.specification}`).join('\n')}\n\n`;
    }

    // Engineering justification
    const justifications: string[] = [];
    if (pressure > 5000) {
      justifications.push(`10K PSI-rated equipment selected because wellhead pressure of ${rfqState.wellheadPressure} PSI exceeds standard 5K threshold per API 6A pressure class requirements.`);
    }
    if (rfqState.h2sPresence === 'yes') {
      justifications.push(`NACE MR0175-compliant materials and H₂S safety systems mandated because confirmed sour service conditions require all wetted components to meet sulfide stress cracking resistance standards.`);
    }
    if (rfqState.sandProduction === 'high') {
      justifications.push(`Full sand management system (Desander + Filter + Debris Catcher) deployed because high sand production levels require multi-stage solids removal to protect downstream equipment and maintain flow assurance.`);
    }
    if (gasRate > 30) {
      justifications.push(`Upgraded flare system specified because gas rate of ${rfqState.gasRate} MMSCFD exceeds standard burner capacity. Flare sizing per API RP 521 thermal radiation limits.`);
    }
    if (rfqState.locationType === 'remote' || rfqState.locationType === 'desert') {
      justifications.push(`Self-contained power and logistics package included because remote/desert location requires autonomous operations with no reliance on external infrastructure.`);
    }
    if (rfqState.environment === 'offshore') {
      justifications.push(`Marine-rated equipment and offshore logistics coordination required. All lifting equipment certified per LOLER requirements.`);
    }
    if (justifications.length === 0) {
      justifications.push(`Standard configuration applied. All equipment rated per stated operational conditions and applicable API/ASME standards.`);
    }

    // Project conditions table for technical response
    const techCondLines: string[] = [];
    techCondLines.push('Parameter | Value | Engineering Implication');
    techCondLines.push('--- | --- | ---');
    if (rfqState.wellheadPressure) {
      const pImpl = pressure > 10000 ? 'Engineering review required — custom design' : pressure > 5000 ? '10K PSI equipment class required' : 'Standard pressure rating adequate';
      techCondLines.push(`Wellhead Pressure | ${rfqState.wellheadPressure} PSI | ${pImpl}`);
    }
    if (rfqState.oilRate) {
      const oilVal = parseFloat(rfqState.oilRate) || 0;
      const oImpl = oilVal > 20000 ? 'Maximum capacity separator — sizing review' : oilVal > 5000 ? 'High capacity separator required' : 'Standard separator capacity';
      techCondLines.push(`Oil Rate | ${rfqState.oilRate} BOPD | ${oImpl}`);
    }
    if (rfqState.gasRate) {
      const gImpl = gasRate > 50 ? 'Custom flare engineering design' : gasRate > 30 ? 'Upgraded flare system' : 'Standard flare adequate';
      techCondLines.push(`Gas Rate | ${rfqState.gasRate} MMSCFD | ${gImpl}`);
    }
    if (rfqState.temperature) techCondLines.push(`Temperature | ${rfqState.temperature}°C | Material selection consideration`);
    if (rfqState.sandProduction) {
      const sImpl = rfqState.sandProduction === 'high' ? 'Full sand management system' : rfqState.sandProduction === 'medium' ? 'Sand filter required' : 'No sand management needed';
      techCondLines.push(`Sand Production | ${rfqState.sandProduction} | ${sImpl}`);
    }
    techCondLines.push(`H₂S | ${rfqState.h2sPresence === 'yes' ? 'Present' : 'Not indicated'} | ${rfqState.h2sPresence === 'yes' ? 'NACE MR0175 compliance mandatory' : 'Standard materials acceptable'}`);

    // Build execution model
    const execModel = rfqState.serviceModel === 'full-service'
      ? `Mobilization → Site preparation → Equipment rig-up → Commissioning → Operations → Demobilization.\n\nCrew composition: Dedicated Supervisor, 2–4 Trained Operators, HSE Officer (full-time). All personnel IADC/IWCF certified where applicable. Operations conducted under EnergiPro IMS with daily reporting to client representative.`
      : `Mobilization → Equipment delivery → Client rig-up (EnergiPro technical support available) → Operations (client-managed) → Demobilization.\n\nEquipment delivered with full documentation package including certification, operating procedures, and maintenance schedules. EnergiPro technical support available on-call.`;

    // Engineering notes
    const engNotes: string[] = [];
    if (ruleOutput.flags.length > 0) {
      ruleOutput.flags.forEach(f => engNotes.push(f.replace(/⚠️\s*/g, '')));
    }
    if (pressure > 10000) {
      engNotes.push('Pressure exceeds 10,000 PSI. Detailed engineering review and custom equipment design mandatory before deployment.');
    }
    if (rfqState.environment === 'offshore') {
      engNotes.push('Offshore deployment requires marine-certified equipment, deck load calculations, and logistics coordination with vessel operator.');
    }
    engNotes.push('All operations to comply with local regulatory requirements and client HSE management system.');
    if (engNotes.length === 1) {
      engNotes.unshift('No critical engineering flags identified for this configuration.');
    }

    const technicalResponse = [
      '1. EXECUTIVE SUMMARY',
      '',
      `EnergiPro Solutions provides ${serviceLabel} for ${envLabel.toLowerCase()} operations in ${countryLabel}. Configuration is driven by ${pressure > 5000 ? `high-pressure conditions (${rfqState.wellheadPressure} PSI)` : rfqState.h2sPresence === 'yes' ? 'confirmed H₂S presence requiring sour service compliance' : 'stated operational parameters'}. ${ruleOutput.packageLevel}. Delivery confidence: High — standard operational scope within EnergiPro capability matrix.`,
      '',
      '2. PROJECT CONDITIONS',
      '',
      techCondLines.join('\n'),
      '',
      '3. SELECTED EQUIPMENT CONFIGURATION',
      '',
      equipSection.trim(),
      '',
      '4. ENGINEERING JUSTIFICATION',
      '',
      justifications.join('\n\n'),
      '',
      '5. OPERATIONAL EXECUTION MODEL',
      '',
      execModel,
      '',
      `Duration: ${durationLabel}. ${rfqState.timeline === 'immediate' ? 'Expedited mobilization protocol activated.' : 'Standard mobilization timeline.'}`,
      '',
      '6. ENGINEERING NOTES',
      '',
      engNotes.join('\n'),
      '',
      '7. ASSUMPTIONS & CLARIFICATIONS',
      '',
      '- Final equipment specification subject to receipt of detailed well data and completion program',
      '- Mobilization timeline assumes equipment availability at time of confirmation',
      '- Site access, crane availability, and laydown area to be confirmed by client',
      '- All certifications and third-party inspections current at time of mobilization',
      ruleOutput.flags.length > 0 ? '- Engineering flags identified above require client confirmation before final design' : '',
      '- This document constitutes a preliminary technical assessment — formal proposal to follow upon client confirmation of scope',
    ].filter(Boolean).join('\n');

    return {
      rfqDraft,
      technicalResponse,
      referenceId: `EP-${Date.now().toString(36).toUpperCase()}`,
      generatedAt: new Date().toISOString(),
      ruleEngineOutput: formatRuleEngineOutput(ruleOutput),
    };
  };

  const handleRetryGeneration = async () => {
    setIsGenerating(true);
    setGenerationError('');
    try {
      const result = await generateAIProposal(rfqState);
      setOutput(result);
    } catch (error: any) {
      setGenerationError(error.message || 'Failed to generate response');
    } finally {
      setIsGenerating(false);
    }
  };

  // Generate RFQ ID in format: EP-{YYYYMMDD}-{random 4 digits}
  const generateRfqId = (): string => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const random = String(Math.floor(1000 + Math.random() * 9000));
    return `EP-${year}${month}${day}-${random}`;
  };

  // Validate required fields before submission
  const validateRfqFields = (): string | null => {
    if (!rfqState.service) return 'Service type is required';
    if (!rfqState.email) return 'Email address is required';
    if (!rfqState.country) return 'Location (country) is required';
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(rfqState.email)) return 'Please enter a valid email address';
    // Validate at least one key parameter
    if (!rfqState.wellheadPressure && !rfqState.oilRate && !rfqState.gasRate) {
      return 'At least one key parameter (pressure, oil rate, or gas rate) is required';
    }
    return null;
  };

  const handleSubmit = async () => {
    // 1. Validate required fields
    const validationError = validateRfqFields();
    if (validationError) {
      setSubmitError(validationError);
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    // 2. Generate RFQ ID (sent as hint; server generates authoritative ID)
    const generatedId = generateRfqId();

    // 3. Build RFQ object
    const rfqPayload = {
      rfqId: generatedId,
      date: new Date().toISOString(),
      clientName: rfqState.fullName,
      company: rfqState.companyName,
      country: rfqState.country,
      serviceType: rfqState.service,
      status: 'new',
      rfqData: rfqState,
      engineeredConfig: ruleEngineOutput,
      clientAdjustments: clientAdjustments,
      adjustmentSummary: buildAdjustmentSummaryText(clientAdjustments),
      validationReport: validationReport,
    };

    try {
      // 4. Submit to backend — saves to database first, then sends email
      const submitResponse = await fetch(`${BACKEND_URL}/api/public/rfq`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(rfqPayload),
      });

      if (!submitResponse.ok) {
        const errData = await submitResponse.json().catch(() => ({ error: 'Server error' }));
        console.warn('[RFQ] Backend submission returned non-OK:', submitResponse.status, errData);
        if (submitResponse.status === 400) {
          setSubmitError(errData.error || 'Validation failed');
          setIsSubmitting(false);
          return;
        }
        throw new Error(errData.error || 'Failed to submit RFQ');
      }

      const responseData = await submitResponse.json();
      console.log('[RFQ] Backend response:', responseData);

      // 5. Use server-returned reference ID (authoritative, from DB)
      const confirmedRfqId = responseData.rfqId || generatedId;
      setRfqId(confirmedRfqId);

      // 6. Mark as submitted only after successful DB save
      if (responseData.success) {
        setIsSubmitted(true);
        if (!responseData.emailSent && responseData.emailPending) {
          setSubmitError('Submission saved successfully. Email confirmation pending — our team will follow up shortly.');
        } else {
          setSubmitError('');
        }
      } else {
        throw new Error('Server did not confirm successful save');
      }

      // 7. Generate AI outputs (proposal generation)
      setIsGenerating(true);
      setGenerationError('');
      try {
        const result = await generateAIProposal(rfqState);
        setOutput(result);
      } catch (aiError: any) {
        // AI failed — use fallback rule-engine-based output
        console.warn('[FALLBACK] AI generation failed, using rule engine output:', aiError.message);
        const fallback = generateFallbackOutput();
        setOutput(fallback);
        setGenerationError('');
      }
    } catch (error: any) {
      console.error('[RFQ] Submission error:', error.message);
      setSubmitError(error.message || 'Failed to submit RFQ. Please try again.');
    } finally {
      setIsSubmitting(false);
      setIsGenerating(false);
    }
  };

  const handleDownload = (content: string, title: string) => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>EnergiPro Solutions - ${title}</title>
            <style>
              body { font-family: 'Segoe UI', sans-serif; padding: 40px; color: #1a1a2e; line-height: 1.8; max-width: 800px; margin: 0 auto; }
              h1 { color: #0f1729; border-bottom: 3px solid #e8650a; padding-bottom: 12px; font-size: 22px; letter-spacing: 1px; }
              h2 { color: #1a1a2e; margin-top: 28px; font-size: 15px; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid #eee; padding-bottom: 6px; }
              table { width: 100%; border-collapse: collapse; margin: 12px 0; font-size: 13px; }
              th { text-align: left; padding: 8px 12px; background: #f5f5f5; border-bottom: 2px solid #ddd; font-weight: 600; text-transform: uppercase; font-size: 11px; letter-spacing: 0.5px; }
              td { padding: 8px 12px; border-bottom: 1px solid #eee; }
              .header { text-align: center; margin-bottom: 40px; }
              .header p { color: #666; font-size: 13px; }
              .ref { color: #666; font-size: 11px; margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; }
              .note-box { background: #fff8e1; border-left: 4px solid #f59e0b; padding: 12px 16px; margin: 12px 0; font-size: 13px; }
              pre { white-space: pre-wrap; font-family: inherit; font-size: 13px; line-height: 1.8; }
              .section-num { color: #e8650a; font-weight: 700; margin-right: 8px; }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>ENERGIPRO SOLUTIONS</h1>
              <p>${title}</p>
            </div>
            <pre>${content}</pre>
            <div class="ref">
              <p>Reference ID: ${output?.referenceId || rfqId}</p>
              <p>Generated: ${new Date().toLocaleString()}</p>
              <p>PRELIMINARY — Subject to engineering review and formal confirmation.</p>
              <p>This document does not constitute a commercial offer or binding commitment.</p>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-[#0a0f1e]">
        {/* ─── DEMO MODE BANNER ─────────────────────────────────────── */}
        {demoActive && selectedDemo && (
          <div className="bg-gradient-to-r from-orange-600 to-orange-700 py-3 px-4 text-center">
            <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Play size={16} className="text-white" />
                <span className="text-white font-bold text-sm">DEMO MODE — {selectedDemo.title}</span>
              </div>
              <div className="flex items-center gap-3">
                <select
                  className="bg-white/20 border border-white/30 text-white text-xs rounded px-3 py-1.5 focus:outline-none"
                  value={selectedDemo.id}
                  onChange={(e) => {
                    const demo = DEMO_SCENARIOS.find(d => d.id === e.target.value);
                    if (demo) {
                      setSelectedDemo(demo);
                      setRfqState(demo.state);
                      setCurrentStep(6);
                    }
                  }}
                >
                  {DEMO_SCENARIOS.map(d => (
                    <option key={d.id} value={d.id} className="text-slate-900">{d.title}</option>
                  ))}
                </select>
                <button
                  onClick={() => {
                    setDemoActive(false);
                    setSelectedDemo(null);
                    setRfqState(initialRFQState);
                    setCurrentStep(0);
                  }}
                  className="text-white/80 hover:text-white text-xs underline"
                >
                  Exit Demo
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ─── HEADER ─────────────────────────────────────────────────── */}
        <section className="relative py-16 bg-gradient-to-b from-[#0f1729] to-[#0a0f1e] border-b border-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-orange-500/10 border border-orange-500/30 rounded-full mb-6">
              <Cpu className="w-4 h-4 text-orange-400" />
              <span className="text-orange-300 text-sm font-medium tracking-wide">ENGINEERING-DRIVEN</span>
            </div>
            <h1 className="font-['Oswald'] text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-4">
              SMART RFQ BUILDER
            </h1>
            <p className="text-slate-400 text-lg max-w-3xl mx-auto mb-4">
              Structure your requirement and receive a preliminary technical response — validated and formatted for tender submission.
            </p>
            {/* Tender Positioning */}
            <p className="text-orange-400/80 text-sm max-w-2xl mx-auto mb-8">
              Pre-execution validation · Risk-aware configuration · Engineering-driven decisions
            </p>
            {/* Trust Indicators */}
            <div className="flex flex-wrap justify-center gap-6">
              <div className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg">
                <Shield className="w-4 h-4 text-green-400" />
                <span className="text-slate-300 text-sm">IMS-Controlled Handling</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg">
                <Lock className="w-4 h-4 text-blue-400" />
                <span className="text-slate-300 text-sm">Tender-Grade Documentation</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg">
                <ClipboardList className="w-4 h-4 text-orange-400" />
                <span className="text-slate-300 text-sm">Engineering-Reviewed Response</span>
              </div>
            </div>

            {/* WhatsApp Quick Contact */}
            <div className="mt-8 flex justify-center">
              <a
                href="https://wa.me/966552693829?text=Hello%20EnergiPro%2C%20I%20have%20an%20inquiry"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-6 py-3 bg-[#25D366]/10 border border-[#25D366]/30 rounded-lg hover:bg-[#25D366]/20 transition-all group"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" fill="currentColor" className="w-5 h-5 text-[#25D366]"><path d="M16.004 0h-.008C7.174 0 0 7.176 0 16.004c0 3.5 1.128 6.744 3.046 9.378L1.054 31.29l6.118-1.958a15.92 15.92 0 0 0 8.832 2.666C24.826 31.998 32 24.822 32 15.994 32 7.176 24.826 0 16.004 0zm9.53 22.606c-.4 1.126-2.342 2.154-3.226 2.254-.884.1-1.702.4-5.73-1.194-4.852-1.918-7.926-6.89-8.164-7.208-.238-.318-1.946-2.588-1.946-4.938 0-2.35 1.232-3.508 1.67-3.986.438-.478.954-.598 1.272-.598.318 0 .636.002.914.016.294.014.688-.112 1.076.82.4.954 1.352 3.304 1.472 3.544.12.238.198.518.04.836-.16.318-.238.518-.478.796-.238.278-.502.622-.718.834-.238.238-.488.496-.21.974.278.478 1.234 2.036 2.65 3.298 1.82 1.622 3.354 2.124 3.832 2.362.478.238.756.2 1.034-.12.278-.318 1.194-1.392 1.512-1.87.318-.478.636-.398 1.074-.238.438.16 2.786 1.314 3.264 1.552.478.238.796.358.914.558.12.198.12 1.152-.278 2.278z"/></svg>
                <span className="text-[#25D366] font-medium text-sm group-hover:text-green-300 transition-colors">Quick Inquiry via WhatsApp</span>
              </a>
            </div>
          </div>
        </section>

        {/* ─── MAIN CONTENT ───────────────────────────────────────────── */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {!isSubmitted ? (
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
              {/* LEFT: Form Panel (3 cols) */}
              <div className="lg:col-span-3">
                {/* Progress Bar */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-3">
                    {STEP_LABELS.map((label, index) => (
                      <div key={index} className="flex flex-col items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                          index < currentStep ? 'bg-orange-500 text-white' :
                          index === currentStep ? 'bg-orange-500 text-white ring-4 ring-orange-500/30' :
                          'bg-slate-700 text-slate-400'
                        }`}>
                          {index < currentStep ? '✓' : index + 1}
                        </div>
                        <span className={`text-xs mt-1.5 hidden sm:block ${
                          index <= currentStep ? 'text-orange-300' : 'text-slate-500'
                        }`}>{label}</span>
                      </div>
                    ))}
                  </div>
                  <div className="w-full bg-slate-700 rounded-full h-1.5">
                    <div
                      className="bg-gradient-to-r from-orange-500 to-orange-400 h-1.5 rounded-full transition-all duration-500"
                      style={{ width: `${(currentStep / TOTAL_STEPS) * 100}%` }}
                    />
                  </div>
                </div>

                {/* Step Content */}
                <div className="bg-[#111827] border border-slate-700/50 rounded-xl p-6 md:p-8 min-h-[420px]">
                  <div className="mb-6">
                    <span className="text-orange-400 text-sm font-semibold tracking-wider uppercase">Step {currentStep + 1} of {TOTAL_STEPS + 1}</span>
                    <h2 className="font-['Oswald'] text-2xl text-white mt-1">{STEP_LABELS[currentStep]}</h2>
                  </div>

                  {/* STEP 0: Service Selection */}
                  {currentStep === 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {SERVICE_TYPES.map(service => (
                        <button
                          key={service.id}
                          onClick={() => updateField('service', service.id)}
                          className={`relative p-4 rounded-lg border text-left transition-all duration-200 hover:scale-[1.02] ${
                            rfqState.service === service.id
                              ? 'bg-orange-500/10 border-orange-500 ring-1 ring-orange-500/50'
                              : 'bg-slate-800/50 border-slate-600 hover:border-slate-500 hover:bg-slate-800'
                          }`}
                        >
                          <span className="text-xl mb-2 block">{service.icon}</span>
                          <span className={`text-sm font-medium ${
                            rfqState.service === service.id ? 'text-orange-300' : 'text-slate-300'
                          }`}>{service.label}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* STEP 1: Operational Conditions */}
                  {currentStep === 1 && (
                    <div className="space-y-5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-slate-400 text-sm mb-1.5">Wellhead Pressure (PSI)</label>
                          <input
                            type="number"
                            value={rfqState.wellheadPressure}
                            onChange={e => updateField('wellheadPressure', e.target.value)}
                            placeholder="e.g. 5000"
                            className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500/50"
                          />
                        </div>
                        <div>
                          <label className="block text-slate-400 text-sm mb-1.5">Oil Rate (BOPD)</label>
                          <input
                            type="number"
                            value={rfqState.oilRate}
                            onChange={e => updateField('oilRate', e.target.value)}
                            placeholder="e.g. 10000"
                            className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500/50"
                          />
                        </div>
                        <div>
                          <label className="block text-slate-400 text-sm mb-1.5">Gas Rate (MMSCFD)</label>
                          <input
                            type="number"
                            value={rfqState.gasRate}
                            onChange={e => updateField('gasRate', e.target.value)}
                            placeholder="e.g. 25"
                            className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500/50"
                          />
                        </div>
                        <div>
                          <label className="block text-slate-400 text-sm mb-1.5">Temperature (°C)</label>
                          <input
                            type="number"
                            value={rfqState.temperature}
                            onChange={e => updateField('temperature', e.target.value)}
                            placeholder="e.g. 120"
                            className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500/50"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-slate-400 text-sm mb-1.5">Sand Production</label>
                          <div className="flex gap-2">
                            {SAND_PRODUCTION_OPTIONS.map(opt => (
                              <button
                                key={opt.id}
                                onClick={() => updateField('sandProduction', opt.id)}
                                className={`flex-1 py-2.5 rounded-lg border text-sm font-medium transition-all ${
                                  rfqState.sandProduction === opt.id
                                    ? 'bg-orange-500/10 border-orange-500 text-orange-300'
                                    : 'bg-slate-800 border-slate-600 text-slate-400 hover:border-slate-500'
                                }`}
                              >{opt.label}</button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="block text-slate-400 text-sm mb-1.5">H₂S Presence</label>
                          <div className="flex gap-2">
                            {H2S_OPTIONS.map(opt => (
                              <button
                                key={opt.id}
                                onClick={() => updateField('h2sPresence', opt.id)}
                                className={`flex-1 py-2.5 rounded-lg border text-sm font-medium transition-all ${
                                  rfqState.h2sPresence === opt.id
                                    ? 'bg-orange-500/10 border-orange-500 text-orange-300'
                                    : 'bg-slate-800 border-slate-600 text-slate-400 hover:border-slate-500'
                                }`}
                              >{opt.label}</button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* STEP 2: Scope */}
                  {currentStep === 2 && (
                    <div className="space-y-6">
                      <div>
                        <label className="block text-slate-400 text-sm mb-2">Environment</label>
                        <div className="flex gap-3">
                          {ENVIRONMENT_OPTIONS.map(opt => (
                            <button
                              key={opt.id}
                              onClick={() => updateField('environment', opt.id)}
                              className={`flex-1 py-3 rounded-lg border text-sm font-medium transition-all ${
                                rfqState.environment === opt.id
                                  ? 'bg-orange-500/10 border-orange-500 text-orange-300'
                                  : 'bg-slate-800 border-slate-600 text-slate-400 hover:border-slate-500'
                              }`}
                            >{opt.label}</button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-slate-400 text-sm mb-2">Service Model</label>
                        <div className="flex gap-3">
                          {SERVICE_MODEL_OPTIONS.map(opt => (
                            <button
                              key={opt.id}
                              onClick={() => updateField('serviceModel', opt.id)}
                              className={`flex-1 py-3 rounded-lg border text-sm font-medium transition-all ${
                                rfqState.serviceModel === opt.id
                                  ? 'bg-orange-500/10 border-orange-500 text-orange-300'
                                  : 'bg-slate-800 border-slate-600 text-slate-400 hover:border-slate-500'
                              }`}
                            >{opt.label}</button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-slate-400 text-sm mb-2">Duration</label>
                        <div className="flex gap-3">
                          {DURATION_OPTIONS.map(opt => (
                            <button
                              key={opt.id}
                              onClick={() => updateField('duration', opt.id)}
                              className={`flex-1 py-3 rounded-lg border text-sm font-medium transition-all ${
                                rfqState.duration === opt.id
                                  ? 'bg-orange-500/10 border-orange-500 text-orange-300'
                                  : 'bg-slate-800 border-slate-600 text-slate-400 hover:border-slate-500'
                              }`}
                            >{opt.label}</button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* STEP 3: Location */}
                  {currentStep === 3 && (
                    <div className="space-y-6">
                      <div>
                        <label className="block text-slate-400 text-sm mb-2">Country</label>
                        <div className="grid grid-cols-3 gap-2">
                          {COUNTRY_OPTIONS.map(opt => (
                            <button
                              key={opt.id}
                              onClick={() => updateField('country', opt.id)}
                              className={`py-2.5 rounded-lg border text-sm font-medium transition-all ${
                                rfqState.country === opt.id
                                  ? 'bg-orange-500/10 border-orange-500 text-orange-300'
                                  : 'bg-slate-800 border-slate-600 text-slate-400 hover:border-slate-500'
                              }`}
                            >{opt.label}</button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-slate-400 text-sm mb-2">Environment Type</label>
                        <div className="flex gap-3">
                          {LOCATION_TYPE_OPTIONS.map(opt => (
                            <button
                              key={opt.id}
                              onClick={() => updateField('locationType', opt.id)}
                              className={`flex-1 py-3 rounded-lg border text-sm font-medium transition-all ${
                                rfqState.locationType === opt.id
                                  ? 'bg-orange-500/10 border-orange-500 text-orange-300'
                                  : 'bg-slate-800 border-slate-600 text-slate-400 hover:border-slate-500'
                              }`}
                            >{opt.label}</button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* STEP 4: Timeline */}
                  {currentStep === 4 && (
                    <div className="space-y-6">
                      <div>
                        <label className="block text-slate-400 text-sm mb-2">Required Timeline</label>
                        <div className="flex gap-3">
                          {TIMELINE_OPTIONS.map(opt => (
                            <button
                              key={opt.id}
                              onClick={() => updateField('timeline', opt.id)}
                              className={`flex-1 py-4 rounded-lg border text-sm font-medium transition-all ${
                                rfqState.timeline === opt.id
                                  ? 'bg-orange-500/10 border-orange-500 text-orange-300'
                                  : 'bg-slate-800 border-slate-600 text-slate-400 hover:border-slate-500'
                              }`}
                            >{opt.label}</button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* STEP 5: Additional Notes + Contact */}
                  {currentStep === 5 && (
                    <div className="space-y-5">
                      <div>
                        <label className="block text-slate-400 text-sm mb-1.5">Additional Notes / Special Requirements</label>
                        <textarea
                          value={rfqState.additionalNotes}
                          onChange={e => updateField('additionalNotes', e.target.value)}
                          placeholder="Any special requirements, well conditions, specific equipment needs, or additional context..."
                          rows={4}
                          className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500/50 resize-none"
                        />
                      </div>
                      <div className="border-t border-slate-700 pt-5">
                        <h3 className="text-white font-medium mb-3">Contact Information</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <input
                            type="text"
                            value={rfqState.fullName}
                            onChange={e => updateField('fullName', e.target.value)}
                            placeholder="Full Name"
                            className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500/50"
                          />
                          <input
                            type="text"
                            value={rfqState.companyName}
                            onChange={e => updateField('companyName', e.target.value)}
                            placeholder="Company Name"
                            className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500/50"
                          />
                          <input
                            type="email"
                            value={rfqState.email}
                            onChange={e => updateField('email', e.target.value)}
                            placeholder="Email Address"
                            className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500/50"
                          />
                          <input
                            type="tel"
                            value={rfqState.phone}
                            onChange={e => updateField('phone', e.target.value)}
                            placeholder="Phone Number"
                            className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500/50"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* STEP 6: Review & Adjust Configuration */}
                  {currentStep === 6 && (
                    <ReviewAdjustStep
                      ruleOutput={ruleEngineOutput}
                      adjustments={clientAdjustments}
                      onAdjustmentsChange={setClientAdjustments}
                      h2sPresence={rfqState.h2sPresence}
                      wellheadPressure={rfqState.wellheadPressure}
                    />
                  )}

                  {/* STEP 7: Engineering Validation */}
                  {currentStep === 7 && validationReport && (
                    <ValidationPanel
                      report={validationReport}
                      onGoBack={() => setCurrentStep(6)}
                    />
                  )}

                  {/* Submit Error Display */}
                  {submitError && (
                    <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                      <span className="text-red-300 text-sm">{submitError}</span>
                    </div>
                  )}

                  {/* Navigation Buttons */}
                  <div className="flex justify-between items-center mt-8 pt-6 border-t border-slate-700/50">
                    <button
                      onClick={handleBack}
                      disabled={currentStep === 0}
                      className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-sm transition-all ${
                        currentStep === 0
                          ? 'text-slate-600 cursor-not-allowed'
                          : 'text-slate-300 bg-slate-800 border border-slate-600 hover:bg-slate-700'
                      }`}
                    >
                      <ArrowLeft className="w-4 h-4" /> Back
                    </button>
                    {currentStep < TOTAL_STEPS ? (
                      <button
                        onClick={handleNext}
                        disabled={!canProceed()}
                        className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium text-sm transition-all ${
                          canProceed()
                            ? 'bg-orange-500 text-white hover:bg-orange-600 shadow-lg shadow-orange-500/20'
                            : 'bg-slate-700 text-slate-500 cursor-not-allowed'
                        }`}
                      >
                        {currentStep === 6 ? 'Run Validation' : 'Next'} <ArrowRight className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        onClick={handleSubmit}
                        disabled={isSubmitting || !validationReport?.canSubmit}
                        className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium text-sm transition-all ${
                          validationReport?.canSubmit
                            ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700 shadow-lg shadow-orange-500/20'
                            : 'bg-slate-700 text-slate-500 cursor-not-allowed'
                        } disabled:opacity-50`}
                      >
                        {isSubmitting ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4" /> Submit RFQ
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* RIGHT: Live Output Panel (2 cols) */}
              <div className="lg:col-span-2">
                <div className="bg-[#111827] border border-slate-700/50 rounded-xl p-6 sticky top-24">
                  <div className="flex items-center gap-2 mb-5">
                    <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                    <span className="text-green-400 text-sm font-medium">LIVE OUTPUT</span>
                  </div>

                  {/* Draft RFQ Summary */}
                  {liveRFQSummary.length > 0 && (
                    <div className="mb-5">
                      <h3 className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2">Draft RFQ Summary</h3>
                      <div className="bg-slate-800/50 rounded-lg p-3">
                        {liveRFQSummary.map((line, i) => (
                          <p key={i} className="text-slate-300 text-xs py-0.5 flex items-start gap-1.5">
                            <span className="text-orange-400 mt-0.5">▸</span>
                            {line}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recommended Configuration */}
                  {ruleEngineOutput.equipmentList.length > 0 && (
                    <div className="mb-5">
                      <h3 className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2">Recommended Technical Configuration</h3>
                      <div className="bg-slate-800/50 rounded-lg p-3 max-h-40 overflow-y-auto">
                        <p className="text-orange-300 text-xs font-medium mb-2">{ruleEngineOutput.packageLevel}</p>
                        {ruleEngineOutput.equipmentList.map((item, i) => (
                          <div key={i} className="flex items-start gap-2 py-0.5">
                            <span className="text-orange-400 text-xs mt-0.5">▸</span>
                            <div>
                              <span className="text-white text-xs">{item.name}</span>
                              <span className="text-slate-500 text-xs ml-1">— {item.specification}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Key Engineering Notes */}
                  {ruleEngineOutput.technicalNotes.length > 0 && (
                    <div className="mb-5">
                      <h3 className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2">Key Engineering Notes</h3>
                      <div className="space-y-1">
                        {ruleEngineOutput.technicalNotes.slice(0, 5).map((note, i) => (
                          <p key={i} className="text-slate-300 text-xs flex items-start gap-1.5">
                            <span className="text-blue-400 mt-0.5">•</span>
                            {note}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Flags */}
                  {ruleEngineOutput.flags.length > 0 && (
                    <div>
                      <h3 className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2">Flags & Alerts</h3>
                      <div className="space-y-1.5">
                        {ruleEngineOutput.flags.map((flag, i) => (
                          <div key={i} className="flex items-start gap-2 bg-red-500/5 border border-red-500/20 rounded-lg px-3 py-2">
                            <AlertTriangle className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" />
                            <span className="text-red-300 text-xs">{flag}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Empty State */}
                  {ruleEngineOutput.equipmentList.length === 0 && liveRFQSummary.length === 0 && (
                    <div className="text-center py-12">
                      <Cpu className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                      <p className="text-slate-500 text-sm">Select a service to see the<br />engineered configuration</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            /* ─── POST-SUBMISSION VIEW ─────────────────────────────────── */
            <div className="max-w-5xl mx-auto">
              {/* Success Confirmation — Data saved to database */}
              <div className={`bg-[#111827] border ${submitError ? 'border-yellow-500/30' : 'border-green-500/30'} rounded-xl p-8 mb-8`}>
                <div className="flex items-center gap-3 mb-6">
                  <div className={`w-10 h-10 ${submitError ? 'bg-yellow-500/10' : 'bg-green-500/10'} rounded-full flex items-center justify-center`}>
                    <CheckCircle className={`w-5 h-5 ${submitError ? 'text-yellow-400' : 'text-green-400'}`} />
                  </div>
                  <div>
                    <h2 className="text-white font-bold text-xl">
                      {submitError ? 'RFQ Saved — Email Pending' : 'RFQ Saved & Confirmed'}
                    </h2>
                    <p className="text-slate-400 text-sm">Reference ID: <span className="text-orange-300 font-mono">{rfqId}</span></p>
                  </div>
                </div>
                {submitError && (
                  <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <p className="text-yellow-300 text-sm">{submitError}</p>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex items-center gap-2 text-green-300 text-sm">
                    <CheckCircle className="w-4 h-4" /> Data saved to database
                  </div>
                  <div className="flex items-center gap-2 text-green-300 text-sm">
                    <CheckCircle className="w-4 h-4" /> Technical response generated
                  </div>
                  <div className="flex items-center gap-2 text-green-300 text-sm">
                    <CheckCircle className="w-4 h-4" /> Submitted for engineering review
                  </div>
                  <div className="flex items-center gap-2 text-green-300 text-sm">
                    <CheckCircle className="w-4 h-4" /> Reference ID: {rfqId}
                  </div>
                </div>
              </div>

              {/* Loading State */}
              {isGenerating && (
                <div className="bg-[#111827] border border-slate-700/50 rounded-xl p-8 text-center">
                  <div className="w-12 h-12 border-3 border-orange-500/30 border-t-orange-500 rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-white font-medium">Generating Engineering Document...</p>
                  <p className="text-slate-400 text-sm mt-1">Analyzing conditions and building technical configuration</p>
                </div>
              )}

              {/* Error State */}
              {generationError && (
                <div className="bg-[#111827] border border-red-500/30 rounded-xl p-6 mb-6">
                  <p className="text-red-300 text-sm mb-3">{generationError}</p>
                  <button
                    onClick={handleRetryGeneration}
                    disabled={isGenerating}
                    className="flex items-center gap-2 px-4 py-2 bg-orange-500/10 border border-orange-500/30 text-orange-300 rounded-lg text-sm font-medium hover:bg-orange-500/20 transition-all disabled:opacity-50"
                  >
                    {isGenerating ? 'Retrying...' : 'Retry AI Generation'}
                  </button>
                </div>
              )}

              {/* Generated Outputs */}
              {output && !showProposal && (
                <div className="space-y-8">
                  {/* PROPOSAL DOCUMENT BUTTON */}
                  <div className="bg-[#111827] border border-orange-500/40 rounded-xl p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <BookOpen className="w-6 h-6 text-orange-400" />
                        <div>
                          <h3 className="text-white font-bold text-lg">Professional Technical Proposal</h3>
                          <p className="text-slate-400 text-sm">Client-ready document — 12 structured sections</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => {
                            const doc = generateProposalDocument(rfqState, ruleEngineOutput, clientAdjustments, validationReport, output.referenceId);
                            setProposalDoc(doc);
                            setShowProposal(true);
                          }}
                          className="flex items-center gap-2 px-5 py-2.5 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 shadow-lg shadow-orange-500/20 transition-all"
                        >
                          <FileText className="w-4 h-4" /> View Proposal
                        </button>
                        <button
                          onClick={() => {
                            const doc = generateProposalDocument(rfqState, ruleEngineOutput, clientAdjustments, validationReport, output.referenceId);
                            generateProposalPDF(doc, rfqState, ruleEngineOutput);
                          }}
                          className="flex items-center gap-2 px-5 py-2.5 bg-slate-700 border border-slate-600 text-slate-200 rounded-lg text-sm font-medium hover:bg-slate-600 transition-all"
                        >
                          <Download className="w-4 h-4" /> Download PDF
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* CLIENT RESPONSE EMAIL */}
                  <div className="bg-[#111827] border border-slate-700/50 rounded-xl overflow-hidden">
                    <div className="flex items-center justify-between p-5 border-b border-slate-700/50">
                      <div className="flex items-center gap-3">
                        <Mail className="w-5 h-5 text-green-400" />
                        <div>
                          <h3 className="text-white font-bold">Client Response Email</h3>
                          <p className="text-slate-400 text-xs">Professional technical response — ready to send</p>
                        </div>
                      </div>
                      <span className="px-3 py-1 rounded-full text-[10px] font-bold uppercase bg-green-500/10 text-green-300 border border-green-500/30">New</span>
                    </div>
                    <div className="p-4">
                      <EmailPreview
                        rfqState={rfqState}
                        ruleOutput={ruleEngineOutput}
                        referenceId={output.referenceId}
                      />
                    </div>
                  </div>

                  {/* A) Client RFQ Draft */}
                  <div className="bg-[#111827] border border-slate-700/50 rounded-xl overflow-hidden">
                    <div className="flex items-center justify-between p-5 border-b border-slate-700/50">
                      <div className="flex items-center gap-3">
                        <ClipboardList className="w-5 h-5 text-blue-400" />
                        <div>
                          <h3 className="text-white font-bold">Client RFQ Draft</h3>
                          <p className="text-slate-400 text-xs">Structured request document — ready to issue</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDownload(output.rfqDraft, 'Request for Quotation')}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/30 text-blue-300 rounded-lg text-sm font-medium hover:bg-blue-500/20 transition-all"
                      >
                        <Download className="w-4 h-4" /> Print / Save
                      </button>
                    </div>
                    <div className="p-6 md:p-8">
                      <DocumentRenderer content={output.rfqDraft} variant="rfq" />
                    </div>
                  </div>

                  {/* B) Technical Response */}
                  <div className="bg-[#111827] border border-slate-700/50 rounded-xl overflow-hidden">
                    <div className="flex items-center justify-between p-5 border-b border-slate-700/50">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-orange-400" />
                        <div>
                          <h3 className="text-white font-bold">Preliminary Technical Response</h3>
                          <p className="text-slate-400 text-xs">Ref: {output.referenceId} • {new Date(output.generatedAt).toLocaleString()}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDownload(output.technicalResponse, 'Preliminary Technical Response')}
                        className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition-all"
                      >
                        <Download className="w-4 h-4" /> Print / Save
                      </button>
                    </div>
                    <div className="p-6 md:p-8">
                      <DocumentRenderer content={output.technicalResponse} variant="technical" />
                    </div>
                    {/* Document Footer */}
                    <div className="px-6 md:px-8 pb-6 pt-4 border-t border-slate-700/30">
                      <p className="text-slate-500 text-xs italic">
                        PRELIMINARY — Subject to engineering review and formal confirmation. This document does not constitute a commercial offer.
                      </p>
                    </div>
                  </div>

                  {/* C) Client Adjustments (if any) */}
                  {(() => {
                    const adjText = buildAdjustmentSummaryText(clientAdjustments);
                    if (adjText.includes('No client adjustments')) return null;
                    return (
                      <div className="bg-[#111827] border border-orange-500/30 rounded-xl overflow-hidden">
                        <div className="flex items-center gap-3 p-5 border-b border-orange-500/20">
                          <AlertTriangle className="w-5 h-5 text-orange-400" />
                          <div>
                            <h3 className="text-white font-bold">Client-Requested Adjustments</h3>
                            <p className="text-slate-400 text-xs">Subject to engineering validation</p>
                          </div>
                        </div>
                        <div className="p-6 md:p-8">
                          <DocumentRenderer content={adjText} variant="technical" />
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* FULL PROPOSAL VIEW */}
              {output && showProposal && proposalDoc && (
                <div className="space-y-6">
                  {/* Back Button */}
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => setShowProposal(false)}
                      className="flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-600 text-slate-300 rounded-lg text-sm font-medium hover:bg-slate-700 transition-all"
                    >
                      <ArrowLeft className="w-4 h-4" /> Back to Summary
                    </button>
                    <button
                      onClick={() => generateProposalPDF(proposalDoc, rfqState, ruleEngineOutput)}
                      className="flex items-center gap-2 px-5 py-2.5 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 shadow-lg shadow-orange-500/20 transition-all"
                    >
                      <Download className="w-4 h-4" /> Download PDF
                    </button>
                  </div>
                  <ProposalDocumentViewer proposal={proposalDoc} />
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
};
